/**
 * useDailyPlanning Hook
 *
 * Shared logic for the morning planning wizard and evening review.
 * Pure computation from existing data — no API calls.
 */

import { useMemo, useCallback } from 'react';
import type { CalendarSettings, DailyFocusPriority, EveningReviewNote } from '../types';
import type { EventInstance } from '../types';
import type { SchedulableTaskDetail } from '@/lib/api';

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

interface UseDailyPlanningParams {
  settings: CalendarSettings;
  updateSettings: (update: Partial<CalendarSettings>) => void;
  todayEvents: EventInstance[];
  yesterdayEvents: EventInstance[];
  tasks: SchedulableTaskDetail[];
}

export function useDailyPlanning({
  settings,
  updateSettings,
  todayEvents,
  yesterdayEvents,
  tasks,
}: UseDailyPlanningParams) {
  const today = todayStr();

  const shouldShowMorningWizard = useMemo(() => {
    if (!settings.dailyPlanningEnabled || settings.lastPlanningDate === today) return false;
    const hour = new Date().getHours();
    return hour >= settings.morningWizardWindowStart && hour < settings.morningWizardWindowEnd;
  }, [
    settings.dailyPlanningEnabled,
    settings.lastPlanningDate,
    settings.morningWizardWindowStart,
    settings.morningWizardWindowEnd,
    today,
  ]);

  const shouldShowEveningReview = useMemo(() => {
    if (!settings.eveningReviewEnabled || !settings.eveningReviewAutoTrigger) return false;
    if (settings.lastEveningReviewDate === today) return false;
    const hour = new Date().getHours();
    return hour >= settings.eveningReviewWindowStart && hour < settings.eveningReviewWindowEnd;
  }, [
    settings.eveningReviewEnabled,
    settings.eveningReviewAutoTrigger,
    settings.lastEveningReviewDate,
    settings.eveningReviewWindowStart,
    settings.eveningReviewWindowEnd,
    today,
  ]);

  const dismissEveningReview = useCallback(() => {
    updateSettings({ lastEveningReviewDate: today });
  }, [updateSettings, today]);

  const dismissMorningWizard = useCallback(() => {
    updateSettings({ lastPlanningDate: today });
  }, [updateSettings, today]);

  const incompleteTasks = useMemo(() => tasks.filter((t) => t.status === 'pending'), [tasks]);

  const unscheduledTasks = useMemo(
    () => tasks.filter((t) => t.status === 'pending' && !t.scheduledStart),
    [tasks],
  );

  // Capacity calculations
  const availableHours = useMemo(
    () => Math.max(0, settings.workingHoursEnd - settings.workingHoursStart),
    [settings.workingHoursEnd, settings.workingHoursStart],
  );

  const scheduledHours = useMemo(() => {
    let totalMs = 0;
    for (const evt of todayEvents) {
      if (evt.isAllDay) continue;
      const start = new Date(evt.startAt).getTime();
      const end = new Date(evt.endAt).getTime();
      totalMs += end - start;
    }
    return totalMs / (1000 * 60 * 60);
  }, [todayEvents]);

  const remainingHours = useMemo(
    () => Math.max(0, availableHours - scheduledHours),
    [availableHours, scheduledHours],
  );

  // Focus priorities (date-gated)
  const todayPriorities = useMemo((): string[] => {
    if (settings.dailyFocusPriorities?.date === today) {
      return settings.dailyFocusPriorities.priorities;
    }
    return [];
  }, [settings.dailyFocusPriorities, today]);

  const setTodayPriorities = useCallback(
    (priorities: string[]) => {
      const entry: DailyFocusPriority = { date: today, priorities };
      updateSettings({ dailyFocusPriorities: entry });
    },
    [updateSettings, today],
  );

  // Evening review
  const saveEveningReview = useCallback(
    (reflection: string, completedCount: number, plannedCount: number) => {
      const note: EveningReviewNote = {
        date: today,
        reflection,
        completedCount,
        plannedCount,
      };
      const existing = settings.eveningReviewNotes ?? [];
      // Replace entry for today if exists, otherwise append
      const filtered = existing.filter((n) => n.date !== today);
      const updated = [...filtered, note].slice(-30); // cap at 30
      updateSettings({ eveningReviewNotes: updated });
    },
    [updateSettings, settings.eveningReviewNotes, today],
  );

  return {
    shouldShowMorningWizard,
    dismissMorningWizard,
    shouldShowEveningReview,
    dismissEveningReview,
    incompleteTasks,
    unscheduledTasks,
    availableHours,
    scheduledHours,
    remainingHours,
    todayPriorities,
    setTodayPriorities,
    saveEveningReview,
    todayEvents,
    yesterdayEvents,
  };
}
